import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { episodes, recommendations } from '@/mocks/episodes';
import EpisodeCard from '@/components/feature/EpisodeCard';
import { usePageMeta } from '@/hooks/usePageMeta';

type SearchSort = 'relevance' | 'time-desc';

function scoreEpisode(
  episode: (typeof episodes)[number],
  keyword: string,
): number {
  const q = keyword.toLowerCase();
  let score = 0;
  if (episode.title.toLowerCase().includes(q)) score += 10;
  if (episode.subtitle.toLowerCase().includes(q)) score += 6;
  if (episode.summary.toLowerCase().includes(q)) score += 4;
  if (episode.tags.some((tag) => tag.toLowerCase().includes(q))) score += 3;
  if (episode.synopsis.join(' ').toLowerCase().includes(q)) score += 2;
  if (episode.characters.some((c) => `${c.name}${c.role}${c.desc}`.toLowerCase().includes(q))) score += 2;
  return score;
}

const hotKeywords = ['月夜', '悬疑', '铃', '记忆', '祭典', '深海'];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const initialQuery = params.get('q') ?? '';
  const [input, setInput] = useState(initialQuery);
  const [keyword, setKeyword] = useState(initialQuery);
  const [sort, setSort] = useState<SearchSort>('relevance');

  usePageMeta({
    title: keyword
      ? `搜索「${keyword}」- 无颜之月动漫第一季全集`
      : '搜索剧集 - 无颜之月动漫第一季全集',
    description:
      '在无颜之月动漫第一季全集中搜索剧集标题、剧情简介与相关关键词，快速找到你想看的动画内容。',
    keywords: '无颜之月搜索,动漫剧集搜索,无颜之月动漫,动漫剧情查找',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setKeyword(input);
      if (input) {
        setParams({ q: input }, { replace: true });
      } else {
        setParams({}, { replace: true });
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [input, setParams]);

  const results = useMemo(() => {
    if (!keyword.trim()) return [];
    const matched = episodes
      .map((ep) => ({ ep, score: scoreEpisode(ep, keyword) }))
      .filter((item) => item.score > 0);
    if (sort === 'time-desc') {
      return matched
        .map((item) => item.ep)
        .sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime());
    }
    return matched.sort((a, b) => b.score - a.score).map((item) => item.ep);
  }, [keyword, sort]);

  return (
    <div className="w-full px-4 md:px-8 pt-24 md:pt-28">
      <nav className="flex items-center flex-wrap gap-1 text-xs text-foreground-500 mb-6">
        <Link to="/" className="hover:text-accent-400 transition-colors">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-300">搜索</span>
      </nav>

      <div className="mb-8">
        <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-50">搜索剧集</h1>
        <p className="mt-3 text-sm md:text-base text-foreground-400">
          输入剧集标题、剧情关键词、角色名或标签，快速定位你想看的内容。
        </p>
      </div>

      <div className="relative">
        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500 text-lg"></i>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="例如：月夜、记忆、铃、祭典……"
          className="w-full rounded-lg bg-background-900 border border-primary-900/50 pl-12 pr-4 py-3.5 text-sm text-foreground-100 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/70 transition-colors"
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-foreground-500">热门搜索：</span>
        {hotKeywords.map((word) => (
          <button
            key={word}
            type="button"
            onClick={() => setInput(word)}
            className="rounded-full bg-secondary-500/15 border border-secondary-500/30 px-3 py-1 text-xs text-secondary-200 hover:bg-secondary-500/25 transition-colors whitespace-nowrap cursor-pointer"
          >
            {word}
          </button>
        ))}
      </div>

      {keyword.trim() === '' ? (
        <div className="mt-10 rounded-lg bg-background-900 border border-primary-900/40 py-16 text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-300 mb-4 mx-auto">
            <i className="ri-search-eye-line text-3xl"></i>
          </span>
          <h2 className="font-heading text-lg font-semibold text-foreground-100">开始你的搜索</h2>
          <p className="mt-2 text-sm text-foreground-400">输入关键词，或点击上方热门搜索快速查找剧集。</p>
        </div>
      ) : (
        <div className="mt-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
            <p className="text-sm text-foreground-300">
              共找到 <strong className="text-accent-400 font-semibold">{results.length}</strong> 条与「
              <span className="text-foreground-100">{keyword}</span>」相关的结果
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500">排序：</span>
              <button
                type="button"
                onClick={() => setSort('relevance')}
                className={`rounded-md px-3 py-1.5 text-xs transition-colors whitespace-nowrap cursor-pointer ${
                  sort === 'relevance'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-900 border border-primary-900/40 text-foreground-300'
                }`}
              >
                相关度
              </button>
              <button
                type="button"
                onClick={() => setSort('time-desc')}
                className={`rounded-md px-3 py-1.5 text-xs transition-colors whitespace-nowrap cursor-pointer ${
                  sort === 'time-desc'
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-900 border border-primary-900/40 text-foreground-300'
                }`}
              >
                更新时间
              </button>
            </div>
          </div>

          {results.length === 0 ? (
            <div className="rounded-lg bg-background-900 border border-primary-900/40 py-16 px-4 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500/15 text-primary-300 mb-4 mx-auto">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <h2 className="font-heading text-lg font-semibold text-foreground-100">未找到相关内容</h2>
              <p className="mt-2 text-sm text-foreground-400">
                试试其他关键词，或返回首页浏览全部剧集与推荐内容。
              </p>
              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-950 hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-4-line"></i>
                返回首页
              </Link>

              <div className="mt-10 text-left">
                <h3 className="font-heading text-sm font-semibold text-foreground-200 mb-4">为你推荐</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {recommendations.slice(0, 3).map((rec) => (
                    <Link key={rec.id} to="/category" className="group cursor-pointer">
                      <span className="block w-full h-40 rounded-lg overflow-hidden border border-primary-900/40 group-hover:border-primary-500/60 transition-colors">
                        <img
                          src={rec.cover}
                          alt={`${rec.title} 推荐`}
                          className="w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </span>
                      <span className="mt-2 block text-sm text-foreground-200 group-hover:text-accent-400 transition-colors line-clamp-1">
                        {rec.title}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {results.map((episode) => (
                <EpisodeCard key={episode.id} episode={episode} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}